import { useState } from 'react'
import { Button, Dropdown } from 'react-bootstrap'
import { FaLock } from 'react-icons/fa'
import { useAccount } from '../../context/account'
import { useAuthState } from '../../firebase/hooks'
import { Designed, PromiseVoid } from '../../types'
import { HELP_NOTION_LINKS, logEvent } from '../../utils'
import { IconWrapper } from '../IconWrapper'
import { PremiumPlanPaymentPopup } from '../subscription/PremiumPlanPaymentPopup'
import {
  AuthorizedUidsFieldArray,
  LimitationTypeField,
  LimitedAuthorizationSetting,
} from './LimitedAuthorizationSetting'

type BaseProps =
  | {
      as: 'button'
      size?: 'sm' | 'lg'
    }
  | {
      as: 'dropdown_item'
      size?: undefined
    }

type CollaborativeSettingProps = {
  setShowPlanPopup: (showPlanPopup: boolean) => void
  setShowCollaborativeSettingModal: (
    showCollaborativeSettingModal: boolean
  ) => void
} & BaseProps

/**
 * このコンポーネントは、共同編集の設定ボタンでモーダルのロジックは含まれていないものです。モーダルを含むコンポーネントが使えない状況の場合のみ使用してください。
 */
export const CollaborativeSettingWithoutModal = ({
  as,
  setShowCollaborativeSettingModal,
  setShowPlanPopup,
  ...otherProps
}: Designed<CollaborativeSettingProps>) => {
  const { account } = useAccount()
  const { isSignedIn, showSignInPopup } = useAuthState()
  const Component = as === 'button' ? Button : Dropdown.Item

  return (
    <>
      <Component
        {...otherProps}
        onClick={() => {
          // LaTeXエクスポートの機能アクセス時のイベント収集
          logEvent('tap_collaborative_edit')
          if (account === undefined) throw new Error('Unexpected')
          else if (isSignedIn === false || account === null) showSignInPopup()
          else if (!account.features.collaborative_edit) setShowPlanPopup(true)
          else setShowCollaborativeSettingModal(true)
        }}
        disabled={account === undefined}
      >
        {account?.features.collaborative_edit === true ? (
          <>共同編集</>
        ) : (
          <IconWrapper suffix="共同編集">
            <FaLock />
          </IconWrapper>
        )}
      </Component>
    </>
  )
}

type CollaborativeSettingWithModalProps = {
  sharedPath: string
  saveEditability: () => PromiseVoid
  editableUidsFieldArray: AuthorizedUidsFieldArray
  limitationTypeField: LimitationTypeField
} & BaseProps

/**
 *  このコンポーネントは、共同編集の設定ボタンでモーダルのロジックまで含まれているものです。モダールを含むコンポーネントは、クリックやフォーカスでコンポーネント自体の要素が非表示になる状況（例えばスマホのエディタのヘッダー）ではモーダルが意図しないタイミングで閉じてしまうため使用できません。その場合は、モーダルを含まないコンポーネント（CollaborativeSettingWithoutModal）を利用し、ユーザの操作の影響を受けない場所にモーダルを独自に配置してください。
 */
export const CollaborativeSetting = ({
  sharedPath,
  editableUidsFieldArray,
  limitationTypeField,
  saveEditability,
  as,
  ...otherProps
}: Designed<CollaborativeSettingWithModalProps>) => {
  const { account } = useAccount()
  const { isSignedIn, showSignInPopup } = useAuthState()
  const [showPlanPopup, setShowPlanPopup] = useState(false)
  const [showCollaborativeSettingModal, setShowCollaborativeSettingModal] =
    useState(false)

  const Component = as === 'button' ? Button : Dropdown.Item

  return (
    <>
      <Component
        {...otherProps}
        onClick={() => {
          // LaTeXエクスポートの機能アクセス時のイベント収集
          logEvent('tap_collaborative_edit')
          if (account === undefined) throw new Error('Unexpected')
          else if (isSignedIn === false || account === null) showSignInPopup()
          else if (!account.features.collaborative_edit) setShowPlanPopup(true)
          else setShowCollaborativeSettingModal(true)
        }}
        disabled={account === undefined}
      >
        {account?.features.collaborative_edit === true ? (
          <>共同編集</>
        ) : (
          <IconWrapper suffix="共同編集">
            <FaLock />
          </IconWrapper>
        )}
      </Component>
      <PremiumPlanPaymentPopup
        show={showPlanPopup}
        onHide={() => setShowPlanPopup(false)}
      />
      <LimitedAuthorizationSetting
        title="共同編集の権限設定"
        help={HELP_NOTION_LINKS.COLLABORATIVE_EDIT}
        helpEvent="tap_collaborative_edit_help"
        sharedPath={sharedPath}
        authorizedUidsFieldArray={editableUidsFieldArray}
        limitationTypeField={limitationTypeField}
        show={showCollaborativeSettingModal}
        onHide={async () => {
          setShowCollaborativeSettingModal(false)
          await saveEditability()
        }}
      />
    </>
  )
}
